import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { sendQuoteMessage } from "@/lib/api";
import { AI_AVATAR_URL } from "@/lib/media";

const WELCOME = {
  id: "welcome",
  role: "assistant",
  content:
    "Hi, I'm TrawellAI — I can build you a first quote in under 2 minutes. Which destination are you considering (Dubai, Singapore, Malaysia, Thailand, Hong Kong, Philippines, or Vietnam)?",
};

export default function ChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Hide on dedicated quote page or admin
  const hidden = location.pathname === "/quote" || location.pathname === "/admin";

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    const userMsgId = `u-${Date.now()}`;
    setMessages((prev) => [...prev, { id: userMsgId, role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const res = await sendQuoteMessage({ session_id: sessionId, message: text });
      setSessionId(res.session_id);
      setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: "assistant", content: res.reply }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          role: "assistant",
          content:
            "Sorry, I hit a connection issue. Please try again, or visit our Inquiry page and we'll get back within 24 hrs.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-widget-open-btn"
          className="fixed bottom-6 left-6 z-40 group flex items-center gap-3 bg-[#0B192C] text-white pl-2 pr-5 py-2 shadow-2xl hover:bg-[#162844] transition-all"
          aria-label="Open AI Quote chat"
        >
          <img
            src={AI_AVATAR_URL}
            alt="TrawellAI — instant AI travel quote concierge"
            width="40"
            height="40"
            loading="lazy"
            decoding="async"
            className="h-10 w-10 rounded-full"
          />
          <div className="text-left">
            <div className="font-heading text-sm font-semibold flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-[#FF5238]" /> AI First Quote
            </div>
            <div className="text-[11px] text-slate-300">Instant estimate · 2 min</div>
          </div>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 left-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-white border border-slate-200 shadow-2xl flex flex-col"
          style={{ height: "520px" }}
          data-testid="chat-widget-panel"
        >
          <div className="flex items-center justify-between bg-[#0B192C] text-white px-4 py-3">
            <div className="flex items-center gap-3">
              <img
                src={AI_AVATAR_URL}
                alt="TrawellAI assistant"
                width="36"
                height="36"
                loading="lazy"
                decoding="async"
                className="h-9 w-9 rounded-full"
              />
              <div>
                <div className="font-heading text-sm font-semibold">TrawellAI</div>
                <div className="text-[11px] text-slate-300 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Online
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="chat-widget-close-btn"
              className="text-slate-300 hover:text-white"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FDFCF8]">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#0B192C] text-white"
                      : "bg-white border border-slate-200 text-[#0B192C]"
                  }`}
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 px-3.5 py-2.5 text-sm text-slate-400">
                  <span className="inline-flex gap-1">
                    <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" />
                    <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:120ms]" />
                    <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:240ms]" />
                  </span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSend} className="border-t border-slate-200 p-3 bg-white flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your requirement…"
              data-testid="chat-widget-input"
              className="flex-1 bg-transparent outline-none text-sm text-[#0B192C] placeholder:text-slate-400 px-2 py-2"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="chat-widget-send-btn"
              className="bg-[#FF5238] text-white px-3 py-2 disabled:opacity-50 hover:bg-[#E63E26] transition-colors"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}

      {/* Fallback floating icon if user closed but wants open quick */}
      {false && <MessageCircle />}
    </>
  );
}
